import { Button, CustomSelect, Loader, ValidateInput } from "@/shared";
import { MouseEvent, useEffect, useState } from "react";
import styles from "./AddUserForm.module.scss";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  FormProvider,
} from "react-hook-form";
import { IUser } from "@/entities/users/types";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { IDealer } from "@/entities/dealer";
import React from "react";
import { useForm } from "react-hook-form";
import { IApiError, UserRolesEnum } from "@/app/model/types";
import { addUser, fetchUsers } from "@/entities/users/slice";
import { fetchOneDealer } from "@/entities/dealer";
import { useDrawer } from "@/app/providers/DrawerProvider/useDrawer";

interface AddUserFormProps {
  dealerId: string;
  dealerName?: string;
}

export const AddUserForm: React.FC<AddUserFormProps> = ({
  dealerId,
  dealerName,
}) => {
  const dispatch = useAppDispatch();
  const methods = useForm({ mode: "all" });
  const [dealerItems, setDealerItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isEmailTaken, setIsEmailTaken] = useState<string | null>(null);
  const [isApiDealer, setIsApiDealer] = useState<boolean>(false);
  const { isValid, errors } = methods.formState;
  const { dealersList } = useAppSelector((state) => state.dealers);
  const { error, miniLoading } = useAppSelector((state) => state.users);
  const { roleName, isSkadUser, isTechAdmin } = useAppSelector(
    (state) => state.viewer,
  );

  const { closeDrawer } = useDrawer();

  const { watch } = methods;
  const skadRoles: (keyof typeof UserRolesEnum)[] = ["Admin", "Manager"];

  const changeHandler = (
    field: ControllerRenderProps<FieldValues, string>,
    value: string,
  ) => {
    if (field.name === "role") {
      if (value === "ApiDealer") {
        methods.clearErrors();
        setIsApiDealer(true);
      } else setIsApiDealer(false);
    }
    return field.onChange(value);
  };
  const isSkadRole = skadRoles.includes(watch("role"));

  const resetForm = (e: MouseEvent) => {
    e.preventDefault();
    closeDrawer();
    setIsEmailTaken(null);
    methods.reset();
  };

  const onSubmit = async (
    data: Partial<IUser> & {
      password: string;
      confirmPassword: string;
      role: string;
    },
  ) => {
    setIsEmailTaken(null);
    if (data.password !== data.confirmPassword) {
      methods.setError("confirmPassword", {
        type: "validate",
        message: "Пароли не совпадают",
      });
      return;
    }
    const newUserData = { ...data };
    delete newUserData.confirmPassword;
    if (!newUserData.dealerId) {
      newUserData.dealerId = dealerId;
    }

    const result = await dispatch(addUser(newUserData));

    if (addUser.fulfilled.match(result)) {
      await dispatch(fetchUsers(""));
      if (dealerId) {
        await dispatch(fetchOneDealer(dealerId));
      }
      closeDrawer();
      methods.reset();
    }
    if (addUser.rejected.match(result)) {
      const apiError = result.payload as IApiError;
      setIsEmailTaken(apiError.detail);
    }
  };

  useEffect(() => {
    // Проверяем, что ошибка действительно содержит `message` и строку "уже существует"
    if (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      typeof error.message === "string" &&
      error.message.includes("уже существует")
    ) {
      setIsEmailTaken(error.message);
    } else {
      setIsEmailTaken(null); // Очищаем ошибку, если невалидный email
    }
  }, [error]);

  useEffect(() => {
    const dealerItemsValue = dealersList.reduce((acc, dealer: IDealer) => {
      const currentDealer = {
        id: dealer.id,
        name: dealer.shortName,
      };
      acc.push(currentDealer);
      return acc;
    }, []);
    setDealerItems(dealerItemsValue);
  }, [dealersList]);

  useEffect(() => {
    if (roleName) {
      const userRoles = Object.keys(UserRolesEnum).reduce(
        (acc, role: keyof typeof UserRolesEnum) => {
          const currentRole = {
            id: role,
            name: UserRolesEnum[role as keyof typeof UserRolesEnum],
          };
          if (
            roleName === "Admin" &&
            !dealerId &&
            role !== "Dealer" &&
            role !== "Sales" &&
            role !== "TechAdmin" &&
            role !== "ApiDealer"
          ) {
            acc.push(currentRole);
          }
          if (
            roleName === "Admin" &&
            dealerId &&
            role !== "Admin" &&
            role !== "Manager" &&
            role !== "Marketer" &&
            role !== "TechAdmin" &&
            role !== "ApiDealer"
          ) {
            acc.push(currentRole);
          }
          if (
            roleName === "TechAdmin" &&
            !dealerId &&
            role !== "Dealer" &&
            role !== "Sales" &&
            role !== "ApiDealer"
          ) {
            acc.push(currentRole);
          }
          if (
            roleName === "TechAdmin" &&
            dealerId &&
            role !== "Admin" &&
            role !== "Manager" &&
            role !== "Marketer" &&
            role !== "TechAdmin"
          ) {
            acc.push(currentRole);
          }
          if (
            roleName === "Manager" &&
            role !== "Admin" &&
            role !== "Manager" &&
            role !== "Marketer" &&
            role !== "TechAdmin" &&
            role !== "ApiDealer"
          ) {
            acc.push(currentRole);
          }
          if (
            roleName === "Dealer" &&
            role !== "Admin" &&
            role !== "Manager" &&
            role !== "Marketer" &&
            role !== "TechAdmin" &&
            role !== "ApiDealer"
          ) {
            acc.push(currentRole);
          }
          if (roleName === "Sales") {
            return acc;
          }
          return acc;
        },
        [],
      );
      setRoles(userRoles);
    }
  }, [roleName]);

  if (miniLoading) {
    return <Loader isMain={false} />;
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={styles.formWrapper}
      >
        {!!roles.length && (
          <Controller
            control={methods.control}
            name="role"
            rules={{ required: true }}
            render={({ field }) => (
              <CustomSelect
                items={roles}
                placeholder="Выбор роли"
                handleChange={(value) => {
                  changeHandler(
                    field as ControllerRenderProps<FieldValues, string>,
                    value,
                  );

                  if (skadRoles.includes(value as keyof typeof UserRolesEnum)) {
                    methods.setValue("dealerId", "");
                  }
                }}
              />
            )}
          />
        )}
        {(!isSkadRole || !isTechAdmin) && dealerId && (
          <Controller
            control={methods.control}
            name="dealerId"
            rules={{ required: false }}
            render={({ field }) => (
              <CustomSelect
                items={dealerItems}
                placeholder="Выбор диллера"
                disabled={!isSkadUser || !isTechAdmin || !!dealerId}
                handleChange={(value) =>
                  changeHandler(
                    field as ControllerRenderProps<FieldValues, string>,
                    value,
                  )
                }
                fieldValue={
                  (isSkadUser || isTechAdmin) && !dealerName
                    ? undefined
                    : dealerName
                }
              />
            )}
          />
        )}
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Фамилия"
            name="lastName"
            required={!isApiDealer}
            nameRu="Фамилия"
          />
        </div>
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Имя"
            name="firstName"
            required={!isApiDealer}
            nameRu="Имя"
          />
        </div>
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Отчество"
            name="middleName"
            required={!isApiDealer}
            nameRu="Отчество"
          />
        </div>
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Электронная почта"
            name="email"
            required={!isApiDealer}
            pattern={/^\S+@\S+$/i}
            nameRu="Электронная почта"
          />
        </div>
        {isEmailTaken && (
          <div className={styles.requireRules}>{isEmailTaken}</div>
        )}
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Пароль"
            name="password"
            type="password"
            required
            nameRu="Пароль"
            showGenerateIcon={true}
            pattern={
              /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%~^&_])[A-Za-z\d!@#$%~^&_]{10,}$/
            }
          />
        </div>
        {errors && errors.password && (
          <div className={styles.requireRules}>
            Пароль должен соответствовать требованиям:
            <ul>
              <li>длина пароля 10 символов</li>
              <li>не должен совпадать с предыдущим паролем</li>
              <li>
                должен включать символы: A-Z, a-z, "!", "@", "#", "$", "%", "~",
                "^", "&", "_", 0-9
              </li>
            </ul>
          </div>
        )}
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Подтверждение пароля"
            name="confirmPassword"
            type="password"
            required
            nameRu="Подтверждение пароля"
            showGenerateIcon={true}
          />
        </div>
        {errors && errors.confirmPassword && (
          <div className={styles.requireRules}>Пароли не совпадают</div>
        )}
        <div className={styles.formButtons}>
          <Button
            onClick={resetForm}
            name="Отменить"
            type="button"
            buttontStyle="cancelBtn"
          />
          <Button
            type="submit"
            name="Создать"
            disabled={!isValid}
            buttontStyle="default"
          />
        </div>
      </form>
    </FormProvider>
  );
};
